/**
 * Map site UI language (ISO 639-1) to a typical vehicle-registration country (ISO 3166-1 alpha-2).
 * Used to default license-plate country to match the selected site language.
 */
export const LANGUAGE_TO_COUNTRY = {
    de: 'DE',
    en: 'GB',
    cs: 'CZ',
    sk: 'SK',
    pl: 'PL',
    fr: 'FR',
    it: 'IT',
    es: 'ES',
    nl: 'NL',
    hu: 'HU',
    ro: 'RO',
    bg: 'BG',
    hr: 'HR',
    sl: 'SI',
    uk: 'UA',
    el: 'GR',
    pt: 'PT',
    sv: 'SE',
    da: 'DK',
    fi: 'FI',
    no: 'NO',
    is: 'IS',
    et: 'EE',
    lv: 'LV',
    lt: 'LT',
    mk: 'MK',
    sr: 'RS',
    bs: 'BA',
    sq: 'AL',
    ar: 'SA',
    tr: 'TR',
    he: 'IL',
    zh: 'CN',
    ja: 'JP',
    ko: 'KR',
};

/**
 * Some APIs use "UK", others "GB" for United Kingdom — try both for English UI.
 * @param {Set<string>} codes
 * @returns {string|null}
 */
function resolveUnitedKingdomCode(codes) {
    if (codes.has('GB')) return 'GB';
    if (codes.has('UK')) return 'UK';
    return null;
}

/**
 * @param {string|null|undefined} langCode e.g. "de", "en-GB", "EN"
 * @param {{ code: string }[]} countries from API
 * @returns {string} always a code present in `countries` when non-empty, else "DE"
 */
export function resolveDefaultVehicleCountryCode(langCode, countries) {
    const list = Array.isArray(countries) ? countries : [];
    const codes = new Set(list.map((c) => c && c.code).filter(Boolean));

    const raw = String(langCode || '')
        .toLowerCase()
        .trim()
        .split(/[-_]/)[0];

    const mapped = LANGUAGE_TO_COUNTRY[raw];
    if (mapped) {
        if (mapped === 'GB') {
            const ukCode = resolveUnitedKingdomCode(codes);
            if (ukCode) return ukCode;
        } else if (codes.has(mapped)) {
            return mapped;
        }
    }

    if (raw.length === 2) {
        const upper = raw.toUpperCase();
        if (codes.has(upper)) return upper;
    }

    const first = list[0]?.code;
    if (first && codes.has(first)) return first;

    return codes.has('DE') ? 'DE' : (first || 'DE');
}
